import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, DateiMitVerknuepfungen as Datei, DateiVerknuepfung, Dateityp,
              OrdnerMitBindung as Ordner } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useFormularZustand, useBestaetigung }
  from "@kundeninfo/ui";
import { Auswahl, Chip, Formular, Hinweis } from "@kundeninfo/ui/me-design";
import { fileSizeText, relativeZeit } from "../format.js";
import { Dateiwahl, KiKnopf, LoeschKnopf } from "../components/meHuellen.js";
import { useJumpFocus } from "../components/jumpFocus.js";
import { buildFolderTree } from "../components/documentFiles/folderTree.js";
import type { FolderNode } from "../components/documentFiles/folderTree.js";
import "../components/documentFiles/folderTree.css";
import { DOCUMENT_FILE_TEXT as DF } from "../components/documentFiles/texts.js";
import { ReadOnlyMarker } from "../components/documentFiles/ReadOnlyMarker.js";
import { HistoricalChip } from "../components/historical/HistoricalChip.js";
import { DOCUMENT_TAB } from "./customerOverview/targets.js";
import { ObjektVerweis } from "../components/objektPanel/ObjektVerweis.js";
import { bezugAusAkteZiel } from "../components/objektPanel/objektBezug.js";
import { ROW_TEXT } from "./customerOverview/texts.js";
import { UnternehmensChipsAnzeige, ZuordnungsPanel } from "./UnternehmensZuordnung.js";
import type { ZuordnungsUmfeld } from "./UnternehmensZuordnung.js";
import "./KundeDateien.css";

/** Nur die Klientenmethoden, die dieser Reiter wirklich braucht — so lässt er sich im Test
 *  mit einer Handvoll vi.fn zeichnen (Muster aus KundeNotizen.tsx / KundeAnsprechpartner.tsx). */
export type DateienClient = Pick<ApiClient,
  "listFolders" | "createFolder" | "renameFolder" | "moveFolder" | "deleteFolder"
  | "listFiles" | "uploadFile" | "downloadFile" | "renameFile" | "moveFile"
  | "setFileType" | "deleteFile" | "listFileTypes" | "setFileCompanies">;

/** `unternehmen`: die Zuordnung zu Unternehmen (Mandantentrennung R12, R13) — Kontext, kein Filter;
 *  hochgeladen wird ohne Angabe (der Server ordnet dem aktiven Unternehmen zu).
 *  `oeffneDateiId` und `oeffneOrdnerId`: der Sprung aus der Kundenübersicht — der Reiter beginnt im Ordner der
 *  Datei (`null`/fehlt = Wurzel) und rollt sie nach dem Laden in den Blick. Die Vorgänge einer Datei tragen ihren Verweis
 *  selbst (B3, `ObjektVerweis`). */
export type KundeDateienProps = {
  client: DateienClient; kundeId: string; unternehmen: ZuordnungsUmfeld;
  oeffneDateiId?: string | null; oeffneOrdnerId?: string | null;
};

/** Wie ein Vorgang an Datei und Ordner heißt („Angebot ANG-2026-0003“, ein Rechnungsentwurf „Rechnung Entwurf“). */
const documentName = (v: { kind: DateiVerknuepfung["kind"]; displayNumber: string }): string =>
  ROW_TEXT.firstLink(v.kind, v.displayNumber);

/** Eine ApiError-Meldung herausziehen, sonst ein verständlicher Standardtext. */
function alsMeldung(fehler: unknown, standard: string): string {
  if (fehler instanceof ApiError && fehler.message.trim() !== "") return fehler.message;
  return standard;
}

/** Löst den blob-Download im Browser aus. In jsdom fehlt createObjectURL — dann wird der
 *  Anhang still übergangen (der Testfall prüft ohnehin nur, dass downloadFile lief). */
function speichereBlob(blob: Blob, name: string): void {
  if (typeof URL.createObjectURL !== "function") return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Welche Änderung an einer Datei gerade offen ist. Sie hiessen bis zum 07.09.2026
 * „Zeilen-Aktionen", weil sie in der Zeile aufklappten; seit der Modal-Hausregel stehen
 * sie im strikten Panel — der Zustand ist derselbe geblieben, der Ort nicht.
 */
type ZeilenAktion = { fileId: string; art: "umbenennen" | "verschieben" | "kategorie"; entwurf: string };

/** Titel und Knopfbeschriftung je Änderung — an EINER Stelle, damit der Titel nicht vom
 *  Knopf abweichen kann (dieselbe Idee wie AUSGABEWEGE in RechnungDetail). */
const ZEILEN_AKTION_WORTE: Record<ZeilenAktion["art"],
  { titel: string; knopf: string; feld: string }> = {
  umbenennen: { titel: "Datei umbenennen", knopf: "Speichern", feld: "Neuer Dateiname" },
  verschieben: { titel: "Datei verschieben", knopf: "Verschieben nach", feld: "Zielordner" },
  kategorie: { titel: "Kategorie der Datei", knopf: "Übernehmen",
               feld: "Kategorie der Datei" },
};

/**
 * Der Dateien-Reiter: links der Ordnerbaum je Kunde, rechts die Dateiliste des gewählten
 * Ordners. Oben Upload (in den aktuellen Ordner, optional mit Kategorie) und ein
 * Kategorie-Filter, der QUER über die Ordner geht.
 *
 * Kategorie-Filter-Semantik (Entscheidung T10): Ist eine Kategorie im Filter gewählt, zeigt
 * die Liste alle Dateien DIESER Kategorie kundenweit (folderId wird nicht mitgegeben) —
 * genau das meint „quer über die Ordner". Ohne Filter zeigt sie den gewählten Ordner.
 *
 * Anlegen und Ändern laufen im strikten Modalpanel (Hausregel, Entscheid Dirk vom
 * 06.09.2026): der neue Ordner, und die drei Änderungen an einer Datei (Umbenennen,
 * Verschieben, Kategorie). Sie klappten vorher in der Zeile bzw. unter dem Baum auf, und
 * ein Klick daneben — ein anderer Ordner, ein anderer Kategorie-Filter — nahm den Entwurf
 * lautlos mit. Die Zeilen, die das taten (`ordnerWaehlen`, der Filterwechsel), stehen
 * unverändert da; sie sind nur nicht mehr auszulösen, solange ein Panel steht — und sie
 * bleiben richtig für den Fall, dass der Zustand doch einmal von aussen wechselt.
 *
 * Eigene Wurzelklasse `.kd` (NICHT `.ka`/`.ka__*` teilen — Kollisions-Lehre 01.09., damals an
 * der Wurzel `.ka-neu` der Anlege-Maske; siehe `.kn__` in KundeNotizen).
 */
export function KundeDateien(p: KundeDateienProps): ReactElement {
  const { client, kundeId } = p;

  const [ordner, setOrdner] = useState<Ordner[]>([]);
  /** Der Baum ist geladen — erst dann gilt der Fokus-Sprung auf einen Ordner (`useJumpFocus` verbraucht die erste Liste). */
  const [foldersLoaded, setFoldersLoaded] = useState(false);
  const [dateien, setDateien] = useState<Datei[] | null>(null);
  const [dateitypen, setDateitypen] = useState<Dateityp[]>([]);
  // null = Wurzel; sonst die Ordner-Id.
  const [gewaehlterOrdner, setGewaehlterOrdner] = useState<string | null>(p.oeffneOrdnerId ?? null);
  // "" = kein Filter; sonst eine fileTypeId (quer über die Ordner).
  const [filterTyp, setFilterTyp] = useState("");
  // Kategorie, die einem Upload mitgegeben wird ("" = keine).
  const [uploadTyp, setUploadTyp] = useState("");

  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [arbeitet, setArbeitet] = useState(false);

  const [ordnerAnlegenOffen, setOrdnerAnlegenOffen] = useState(false);
  const [ordnerName, setOrdnerName] = useState("");
  const [zeilenAktion, setZeilenAktion] = useState<ZeilenAktion | null>(null);
  /** Die Datei, deren Unternehmen gerade neu zugeordnet werden — ein EIGENER Zustand: Die
   *  Zeilenaktionen tragen genau einen Textwert, eine Menge passt nicht hinein. */
  const [zuordnungFuer, setZuordnungFuer] = useState<Datei | null>(null);
  /** Der Schreibweg, den `formular.aktion()` gleich ausführt (Ordner anlegen oder eine der
   *  drei Änderungen an einer Datei) — es steht immer nur EIN Panel offen. */
  const auftrag = useRef<() => Promise<void>>(async () => {});
  /** Die Feldnamen einer 422, die zum Feld des offenen Panels gehören. */
  const panelFelder = useRef<readonly string[]>([]);

  /**
   * Formularzustände der zwei Panels (ME-Etappe 3, Aufgabe 3): `laeuft` sperrt das Feld und
   * beide Ausgänge, eine 422 hängt am Feld des Panels (Sprung dorthin), alles andere steht
   * als Meldung im Panel. Keine Version auf diesen Wegen — keine 409.
   */
  const formular = useFormularZustand<true>({
    speichern: async () => { await auftrag.current(); return true; },
    neuLaden: async () => { /* keine 409 auf diesen Wegen */ },
    bekannteFelder: (feld) => panelFelder.current.includes(feld),
  });
  /** Der Befund am (einzigen) Feld des offenen Panels. */
  const panelFeldFehler = Object.values(formular.feldFehler)[0];

  const ordnerLaden = useCallback(async (): Promise<void> => {
    try {
      const { items } = await client.listFolders(kundeId);
      setOrdner(items);
      setFoldersLoaded(true);
    } catch (e) {
      setLadeFehler(alsMeldung(e, "Die Ordner konnten nicht geladen werden."));
    }
  }, [client, kundeId]);

  const dateienLaden = useCallback(async (): Promise<void> => {
    try {
      // Ist ein Kategorie-Filter gesetzt, geht die Abfrage quer über die Ordner (kein
      // folderId); sonst zeigt sie den gewählten Ordner (Wurzel = null).
      const filter = filterTyp !== ""
        ? { fileTypeId: filterTyp }
        : { folderId: gewaehlterOrdner };
      const { items } = await client.listFiles(kundeId, filter);
      setDateien(items);
      setLadeFehler(null);
    } catch (e) {
      setDateien(null);
      setLadeFehler(alsMeldung(e, "Die Dateien konnten nicht geladen werden."));
    }
  }, [client, kundeId, gewaehlterOrdner, filterTyp]);

  useEffect(() => {
    void ordnerLaden();
  }, [ordnerLaden]);

  useEffect(() => {
    void dateienLaden();
  }, [dateienLaden]);

  useJumpFocus("data-file-id", p.oeffneDateiId, dateien);
  // Der Sprung in einen Ordner ohne Datei (Abschnitt „Dateien“ am Vorgang): Der Fokus steht am gewählten Ordner (A-K5).
  useJumpFocus("data-folder-id", p.oeffneDateiId === undefined || p.oeffneDateiId === null ? p.oeffneOrdnerId : null,
    foldersLoaded ? ordner : null);

  // Die Kategorien einmal holen — sie hängen am Kunden nicht. Scheitert das, bleibt der Tab
  // bedienbar; nur Filter und Kategorie-Zuordnung entfallen still (kein Blocker).
  useEffect(() => {
    let abgebrochen = false;
    client.listFileTypes()
      .then((a) => { if (!abgebrochen) setDateitypen(a.items); })
      .catch(() => { /* Kategorien optional — kein Fehlerband */ });
    return () => { abgebrochen = true; };
  }, [client]);

  function ordnerWaehlen(id: string | null): void {
    setGewaehlterOrdner(id);
    // Die Ordnerwahl hebt den Kategorie-Filter auf — sonst zeigte die Liste weiter quer
    // über die Ordner und der Klick auf einen Ordner bliebe wirkungslos.
    setFilterTyp("");
    setZeilenAktion(null);
    setOrdnerAnlegenOffen(false);
  }

  async function ordnerAnlegen(): Promise<void> {
    if (ordnerName.trim() === "" || formular.laeuft) return;
    auftrag.current = async () => {
      await client.createFolder(kundeId, { name: ordnerName.trim(), parentId: gewaehlterOrdner });
    };
    panelFelder.current = ["name", "parentId"];
    if (await formular.aktion() !== true) return;
    setOrdnerName("");
    setOrdnerAnlegenOffen(false);
    setMeldung(null);
    await ordnerLaden();
  }

  function ordnerAnlegenOeffnen(): void {
    formular.zuruecksetzen();
    setOrdnerAnlegenOffen(true);
  }

  async function hochladen(datei: File | undefined): Promise<void> {
    // Die Dateiwahl leert sich nach jeder Wahl selbst (`nachWahlLeeren`), damit dieselbe
    // Datei nach einem Fehler erneut gewählt werden kann.
    if (datei === undefined || arbeitet) return;
    setArbeitet(true);
    setMeldung(null);
    try {
      const optionen: { folderId?: string; fileTypeId?: string } = {};
      if (gewaehlterOrdner !== null) optionen.folderId = gewaehlterOrdner;
      if (uploadTyp !== "") optionen.fileTypeId = uploadTyp;
      await client.uploadFile(kundeId, datei, optionen);
      await dateienLaden();
    } catch (fehler) {
      if (fehler instanceof ApiError && fehler.status === 409) {
        setMeldung(`„${datei.name}" ist bereits abgelegt — eine inhaltsgleiche Doublette.`);
      } else if (fehler instanceof ApiError
          && (fehler.status === 413 || fehler.status === 422)) {
        setMeldung(`„${datei.name}" ist zu groß — es sind höchstens 50 MB je Datei möglich.`);
      } else {
        setMeldung(alsMeldung(fehler, `„${datei.name}" konnte nicht hochgeladen werden.`));
      }
    } finally {
      setArbeitet(false);
    }
  }

  async function herunterladen(d: Datei): Promise<void> {
    setMeldung(null);
    try {
      const { blob, filename } = await client.downloadFile(kundeId, d.id);
      speichereBlob(blob, filename ?? d.originalName);
    } catch (e) {
      setMeldung(alsMeldung(e, `„${d.originalName}" konnte nicht heruntergeladen werden.`));
    }
  }

  /** Der Wert, mit dem eine Änderung öffnet — und zugleich der Vergleichswert für die
   *  Rückfrage vor dem Abbrechen. EINE Stelle: Zwei Ableitungen desselben Werts liefen mit
   *  dem nächsten Feld auseinander, und die Rückfrage fragte dann grundlos. */
  function ausgangswert(art: ZeilenAktion["art"], d: Datei): string {
    if (art === "umbenennen") return d.originalName;
    if (art === "verschieben") return d.folderId ?? "";
    return d.fileTypeId ?? "";
  }

  function aktionStarten(art: ZeilenAktion["art"], d: Datei): void {
    setMeldung(null);
    formular.zuruecksetzen();
    setZeilenAktion({ fileId: d.id, art, entwurf: ausgangswert(art, d) });
  }

  /** Das Speichern der offenen Änderung — je Art ein anderer Schreibweg, derselbe Ablauf. */
  async function aenderungSpeichern(d: Datei): Promise<void> {
    const a = zeilenAktion;
    if (a === null || formular.laeuft) return;
    if (a.art === "umbenennen" && a.entwurf.trim() === "") return;
    const ziel = a.entwurf === "" ? null : a.entwurf;
    auftrag.current = async () => {
      if (a.art === "umbenennen") await client.renameFile(kundeId, d.id, a.entwurf.trim());
      else if (a.art === "verschieben") await client.moveFile(kundeId, d.id, ziel);
      else await client.setFileType(kundeId, d.id, ziel);
    };
    panelFelder.current = a.art === "umbenennen" ? ["name", "originalName"]
      : a.art === "verschieben" ? ["folderId"] : ["fileTypeId"];
    if (await formular.aktion() !== true) return;
    setZeilenAktion(null);
    await dateienLaden();
  }

  const { bestaetigen } = useBestaetigung();
  async function loeschen(d: Datei): Promise<void> {
    // Die Bestätigung nennt die sichtbaren Vorgänge und zählt die verborgenen (470, F3) — die Verknüpfungen gehen mit.
    if (!(await bestaetigen({
      titel: DF.deleteTitle,
      text: DF.deleteText(d.originalName, d.links.map(documentName), d.hiddenLinkCount),
      bestaetigenText: DF.deleteConfirm,
      gefaehrlich: true,
    }))) {
      return;
    }
    setArbeitet(true);
    setMeldung(null);
    try {
      await client.deleteFile(kundeId, d.id);
      await dateienLaden();
    } catch (e) {
      setMeldung(alsMeldung(e, "Die Datei konnte nicht gelöscht werden."));
    } finally {
      setArbeitet(false);
    }
  }

  const typName = useCallback((id: string | null): string | null => {
    if (id === null) return null;
    return dateitypen.find((t) => t.id === id)?.name ?? null;
  }, [dateitypen]);

  const baum = buildFolderTree(ordner);

  /** Die Datei, an der gerade eine Änderung offen steht. */
  const zeilenDatei = zeilenAktion === null
    ? null : (dateien ?? []).find((d) => d.id === zeilenAktion.fileId) ?? null;

  /** Steht eines der beiden Panels offen? */
  const panelOffen = ordnerAnlegenOffen || zeilenDatei !== null || zuordnungFuer !== null;

  /**
   * Die Rückmeldung eines Schreibwegs — sie steht dort, wo gerade gearbeitet wird: bei
   * offenem Panel darin, sonst über der Liste. Hinter dem Schleier wäre die Auskunft
   * „konnte nicht umbenannt werden" sichtbar, aber nicht zu befolgen.
   */
  function meldungsband(): ReactElement | null {
    if (meldung === null) return null;
    return <Hinweis art="fehler">{meldung}</Hinweis>;
  }

  /** Die Meldung des Schreibwegs im offenen Panel. */
  function panelMeldung(): ReactElement | null {
    if (formular.meldung === null) return null;
    return <Hinweis art="fehler">{formular.meldung}</Hinweis>;
  }

  /**
   * Die EINEN Ausgänge der beiden Masken neben dem Speichern (Hausregel „striktes
   * Modalpanel", 06.09.2026): kein Ausgang unter laufendem Schreiben, Rückfrage, sobald
   * etwas Ungespeichertes darin steht. Knopf und Escape gehen durch sie.
   */
  async function ordnerAnlegenAbbrechen(): Promise<void> {
    if (formular.laeuft) return;
    if (!(await darfAbbrechen(ordnerName.trim() !== ""))) return;
    setOrdnerAnlegenOffen(false);
    setOrdnerName("");
  }

  async function zeilenAktionAbbrechen(): Promise<void> {
    if (formular.laeuft) return;
    const beruehrt = zeilenAktion !== null && zeilenDatei !== null
      && zeilenAktion.entwurf !== ausgangswert(zeilenAktion.art, zeilenDatei);
    if (!(await darfAbbrechen(beruehrt))) return;
    setZeilenAktion(null);
  }

  function knotenZeichnen(k: FolderNode<Ordner>): ReactElement {
    const aktiv = filterTyp === "" && gewaehlterOrdner === k.folder.id;
    return (
      <li key={k.folder.id} className="folder-tree__item">
        {/* Übergang P12-2 (docs/ME-DESIGN.md): Navigations-/Auswahlzeile als nativer Knopf —
           me-design hat keinen Menüeintrag (Entscheid 44). */}
        <div className="kd__ordner-zeile">
          {/* `data-folder-id`: Der Sprung „Ordner“ (Übersicht, Abschnitt „Dateien“) setzt den Fokus hierher (A-K5). */}
          <button
            type="button"
            className="folder-tree__folder"
            data-folder-id={k.folder.id}
            aria-current={aktiv ? "true" : undefined}
            disabled={panelOffen}
            onClick={() => ordnerWaehlen(k.folder.id)}
          >
            {k.folder.name}
          </button>
          {/* Ein gemerkter Ablageordner trägt leise das Kennzeichen seines Vorgangs (Spec „Kundenreiter ‚Dateien‘“). */}
          {k.folder.boundTo !== null && <Chip>{documentName(k.folder.boundTo)}</Chip>}
        </div>
        {k.children.length > 0 && (
          <ul className="folder-tree folder-tree--children">
            {k.children.map((kind) => knotenZeichnen(kind))}
          </ul>
        )}
      </li>
    );
  }

  const wurzelAktiv = filterTyp === "" && gewaehlterOrdner === null;

  return (
    <div className="kd">
      <aside className="kd__baum-spalte" aria-label="Ordner">
        <ul className="folder-tree">
          <li className="folder-tree__item">
            {/* Übergang P12-2 (docs/ME-DESIGN.md): Navigations-/Auswahlzeile als nativer Knopf
               — me-design hat keinen Menüeintrag (Entscheid 44). */}
            <button
              type="button"
              className="folder-tree__folder"
              aria-current={wurzelAktiv ? "true" : undefined}
              disabled={panelOffen}
              onClick={() => ordnerWaehlen(null)}
            >
              Alle Dateien
            </button>
          </li>
          {baum.map((k) => knotenZeichnen(k))}
        </ul>
        {ordner.length === 0 && (
          <p className="kd__baum-leer">Noch keine Ordner angelegt.</p>
        )}

        {/* Der ganze Baum, die Leiste und die Zeilenaktionen sind gesperrt, solange eines
            der Panels steht (Fixrunde 1, 07.09.2026): Sie liegen hinter dem Schleier. Der
            Riegel ist mehr als Zierde — er ist der einzige Schutz davor, dass ein Test in
            jsdom (wo nichts den Klick abfängt) hinter das Panel greift und eine klemmende
            Maske grün aussehen lässt. Und `ordnerWaehlen` räumte den Entwurf ab, ohne zu
            fragen. */}
        <KiKnopf className="kd__sekundaer kd__ordner-neu-knopf"
                deaktiviert={panelOffen}
                onClick={ordnerAnlegenOeffnen}>
          + Ordner
        </KiKnopf>
      </aside>

      <section className="kd__liste-spalte" aria-label="Dateien">
        <div className="kd__leiste">
          {/* `me-eingabe art="datei"` (Entscheid 31): gesperrt (nicht abgeschaltet) während
              des Hochladens und hinter einem offenen Panel, nach jeder Wahl geleert. */}
          <Dateiwahl id="kd-upload" className="kd__upload" beschriftung="Datei hochladen"
            gesperrt={arbeitet || panelOffen} nachWahlLeeren
            onDateien={(dateien) => { void hochladen(dateien[0]); }} />

          {/* Die zwei Auswahlen der Leiste: hinter einem offenen Panel GESPERRT (nicht
              abgeschaltet) — sie sind dort nicht zu bedienen, bleiben aber lesbar. */}
          <Auswahl id="kd-upload-typ" name="kd-upload-typ" className="kd__leiste-feld"
            beschriftung="Kategorie für den Upload" wert={uploadTyp} gesperrt={panelOffen}
            onGeaendert={(d) => setUploadTyp(d.wert)}>
            <option value="">(ohne)</option>
            {dateitypen.filter((t) => t.active || t.id === uploadTyp).map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </Auswahl>

          <Auswahl id="kd-filter-typ" name="kd-filter-typ" className="kd__leiste-feld"
            beschriftung="Kategorie-Filter" wert={filterTyp} gesperrt={panelOffen}
            onGeaendert={(d) => { setFilterTyp(d.wert); setZeilenAktion(null); }}>
            <option value="">Alle Kategorien</option>
            {dateitypen.filter((t) => t.active || t.id === filterTyp).map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </Auswahl>
        </div>

        {filterTyp !== "" && (
          <p className="kd__hinweis">
            Kategorie-Filter aktiv — es werden alle passenden Dateien über sämtliche Ordner
            gezeigt.
          </p>
        )}

        {!panelOffen && meldungsband()}
        {ladeFehler !== null && <Hinweis art="fehler">{ladeFehler}</Hinweis>}

        {dateien === null && ladeFehler === null && <p className="kd__hinweis">Lädt …</p>}

        {dateien !== null && dateien.length === 0 && (
          <p className="kd__leer">
            {filterTyp !== ""
              ? "Zu dieser Kategorie sind keine Dateien abgelegt."
              : "In diesem Ordner sind keine Dateien abgelegt."}
          </p>
        )}

        {dateien !== null && dateien.length > 0 && (
          <ul className="kd__dateien">
            {dateien.map((d) => {
              const kategorie = typName(d.fileTypeId);
              return (
                <li key={d.id} className="kd__datei" tabIndex={-1} data-file-id={d.id}>
                  <div className="kd__datei-kopf">
                    <span className="kd__datei-name">{d.originalName}</span>
                    <UnternehmensChipsAnzeige alle={p.unternehmen.alle} companyIds={d.companyIds}
                      className="kd__firmen" />
                    {kategorie !== null && (
                      <Chip className="kd__kategorie">{kategorie}</Chip>
                    )}
                  </div>
                  <div className="kd__datei-meta">
                    <span>{fileSizeText(d.sizeBytes)}</span>
                    <span>hochgeladen von {d.uploadedByName}</span>
                    <span>{relativeZeit(d.uploadedAt)}</span>
                  </div>
                  {(d.links.length > 0 || d.hiddenLinkCount > 0) && (
                    <div className="kd__datei-vorgaenge">
                      {/* B3 (Öffnen statt Springen): je sichtbarer Vorgang sein Name als Text, der Verweis direkt dahinter
                          (fremdes Objekt, 115) — Lieferantenobjekte öffnen als Panel über der Akte, Belege springen in ihren
                          Reiter. Die verborgenen nur als Zahl (F3), ein historischer trägt das Kennzeichen „historisch“. */}
                      {d.links.map((l) => {
                        const tab = DOCUMENT_TAB[l.kind];
                        return (
                          <span key={l.id} className="kd__vorgang">
                            <span>{documentName(l)}</span>
                            {tab !== undefined && (
                              <ObjektVerweis bezug={bezugAusAkteZiel({ customerId: p.kundeId, tab, objectId: l.id,
                                companyId: l.companyId }, l.displayNumber)} onGeaendert={() => { void dateienLaden(); }} />
                            )}
                            {l.historical && <HistoricalChip />}
                          </span>
                        );
                      })}
                      {d.hiddenLinkCount > 0 && <Chip>{DF.hiddenLinks(d.hiddenLinkCount)}</Chip>}
                    </div>
                  )}

                  {/* Die Zeile behält ihre Aktionen IMMER: Was sie ändern, geschieht seit
                      dem 07.09.2026 im Panel (siehe unten) — die Zeile klappt nicht mehr
                      auf und verliert dabei auch keine Knöpfe mehr. Ausnahme Entscheid 518:
                      Eine nur lesbare Datei (`readOnly`, Beleg eines Lieferantenvorgangs ohne
                      Lieferantensicht) bietet nur das Herunterladen und trägt „nur lesend“ mit
                      dem Grund im Sofort-Popup (`ReadOnlyMarker`). */}
                  <div className="kd__datei-aktionen">
                    <KiKnopf className="kd__aktion" deaktiviert={panelOffen}
                            onClick={() => void herunterladen(d)}>
                      Herunterladen
                    </KiKnopf>
                    {d.readOnly ? <ReadOnlyMarker /> : (
                      <>
                        <KiKnopf className="kd__aktion" deaktiviert={panelOffen}
                                onClick={() => aktionStarten("umbenennen", d)}>
                          Umbenennen
                        </KiKnopf>
                        <KiKnopf className="kd__aktion" deaktiviert={panelOffen}
                                onClick={() => aktionStarten("verschieben", d)}>
                          Verschieben
                        </KiKnopf>
                        <KiKnopf className="kd__aktion" deaktiviert={panelOffen}
                                onClick={() => aktionStarten("kategorie", d)}>
                          Kategorie
                        </KiKnopf>
                        <KiKnopf className="kd__aktion" deaktiviert={panelOffen}
                                onClick={() => { setMeldung(null); setZuordnungFuer(d); }}>
                          Unternehmen zuordnen
                        </KiKnopf>
                        <LoeschKnopf className="kd__aktion" deaktiviert={panelOffen}
                          name={`${d.originalName} löschen`} onClick={() => void loeschen(d)} />
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Der neue ORDNER. Ein Anlegen ist eine Bearbeitung im Sinne der Hausregel
          (06.09.2026) — auch wenn nur ein Feld darin steht: Ein Klick auf einen Ordner im
          Baum daneben nahm den halb getippten Namen vorher lautlos mit. */}
      {ordnerAnlegenOffen && (
        <Modal titel="Neuer Ordner" onAbbrechen={ordnerAnlegenAbbrechen}
          abbrechenGesperrt={formular.laeuft}>
          <Formular beschriftung="oben" {...formular.formularProps}>
            <Feld id="kd-ordnername" label="Ordnername" bearbeitbar wert={ordnerName}
              fehler={panelFeldFehler} onChange={setOrdnerName} />
            {/* Wohin er kommt, steht dabei: Angelegt wird IM gewählten Ordner, nicht
                irgendwo — beim Anlegen sieht man den Baum nicht mehr. */}
            <p className="kd__hinweis">
              Wird angelegt in: {gewaehlterOrdner === null
                ? "Alle Dateien (Wurzel)"
                : ordner.find((o) => o.id === gewaehlterOrdner)?.name ?? "gewählter Ordner"}
            </p>
          </Formular>
          {panelMeldung()}
          <ModalFuss speichernText="Anlegen"
            speichernGesperrt={ordnerName.trim() === "" || formular.laeuft}
            abbrechenGesperrt={formular.laeuft}
            onSpeichern={() => { void ordnerAnlegen(); }}
            onAbbrechen={ordnerAnlegenAbbrechen} />
        </Modal>
      )}

      {/* Umbenennen, Verschieben, Kategorie — dieselben drei Änderungen wie vorher, nur im
          Panel statt in der aufgeklappten Zeile. EIN Panel für alle drei: Es kann nur eine
          Änderung zugleich offen stehen (`zeilenAktion` hält genau eine), und drei
          gleichgebaute Rahmen nebeneinander liefen mit dem ersten Fix auseinander. */}
      {zeilenAktion !== null && zeilenDatei !== null && (
        <Modal titel={ZEILEN_AKTION_WORTE[zeilenAktion.art].titel}
          onAbbrechen={zeilenAktionAbbrechen} abbrechenGesperrt={formular.laeuft}>
          <Formular beschriftung="oben" {...formular.formularProps}>
            {/* Der Dateiname steht dabei: Im Panel sieht man die Zeile nicht mehr, an der
                der Klick begann. */}
            <p className="kd__hinweis">{zeilenDatei.originalName}</p>
            {zeilenAktion.art === "umbenennen" && (
              <Feld id="kd-zeilenaktion" label={ZEILEN_AKTION_WORTE.umbenennen.feld} bearbeitbar
                wert={zeilenAktion.entwurf} fehler={panelFeldFehler}
                onChange={(wert) => setZeilenAktion({ ...zeilenAktion, entwurf: wert })} />
            )}
            {zeilenAktion.art === "verschieben" && (
              <Auswahl id="kd-zeilenaktion" name="kd-zeilenaktion"
                beschriftung={ZEILEN_AKTION_WORTE.verschieben.feld} wert={zeilenAktion.entwurf}
                {...(panelFeldFehler !== undefined ? { fehler: panelFeldFehler } : {})}
                onGeaendert={(d) => setZeilenAktion({ ...zeilenAktion, entwurf: d.wert })}>
                <option value="">Wurzel (kein Ordner)</option>
                {ordner.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </Auswahl>
            )}
            {zeilenAktion.art === "kategorie" && (
              <Auswahl id="kd-zeilenaktion" name="kd-zeilenaktion"
                beschriftung={ZEILEN_AKTION_WORTE.kategorie.feld} wert={zeilenAktion.entwurf}
                {...(panelFeldFehler !== undefined ? { fehler: panelFeldFehler } : {})}
                onGeaendert={(d) => setZeilenAktion({ ...zeilenAktion, entwurf: d.wert })}>
                <option value="">(ohne Kategorie)</option>
                {dateitypen
                  .filter((t) => t.active || t.id === zeilenAktion.entwurf)
                  .map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
              </Auswahl>
            )}
          </Formular>
          {panelMeldung()}
          <ModalFuss speichernText={ZEILEN_AKTION_WORTE[zeilenAktion.art].knopf}
            speichernGesperrt={formular.laeuft
              || (zeilenAktion.art === "umbenennen" && zeilenAktion.entwurf.trim() === "")}
            abbrechenGesperrt={formular.laeuft}
            onSpeichern={() => { void aenderungSpeichern(zeilenDatei); }}
            onAbbrechen={zeilenAktionAbbrechen} />
        </Modal>
      )}

      {zuordnungFuer !== null && (
        <ZuordnungsPanel titel="Unternehmen der Datei" was="Datei" umfeld={p.unternehmen}
          ausgang={zuordnungFuer.companyIds}
          onSpeichern={async (ids) => {
            await client.setFileCompanies(kundeId, zuordnungFuer.id, ids);
            setZuordnungFuer(null);
            await dateienLaden();
          }}
          onAbbrechen={() => setZuordnungFuer(null)} />
      )}
    </div>
  );
}
